import React from 'react';
import { Button, ButtonGroup, DropdownButton, Select } from '@ygtechnologies/rubus-ui';
import { faBuilding, faUsersCrown, faXmark } from '@fortawesome/pro-light-svg-icons';
import ScopePicker, { ScopeSelection } from './ScopePicker';
import { ANY_LANGUAGE, ScopeState } from './shared';
import theme from './EmailTemplates.module.scss';

interface ScopeBarProps {
    scope: ScopeState;
    onChange(scope: ScopeState): void;
    /** Language codes the template exists in, offered alongside "any language". */
    languages: string[];
    /** Project / profile ids that already have an override, so they can be marked in the pickers. */
    overriddenProjectIds: Set<number>;
    overriddenProfileIds: Set<number>;
}

/**
 * Sets the context everything below is about: which seller profile, which project, which language. The
 * most specific selection is what gets edited (see editScopeOf). A project belongs to exactly one seller
 * profile, so choosing a project also fixes the profile — the two can never disagree, matching how the
 * backend resolves scope.
 */
const ScopeBar: React.FC<ScopeBarProps> = ({ scope, onChange, languages, overriddenProjectIds, overriddenProfileIds }) => {
    const selectProject = (selection: ScopeSelection) => {
        onChange({
            ...scope,
            projectId: selection.id,
            projectName: selection.name,
            // The project's owner wins over any separately chosen profile.
            profileId: selection.ownerProfileId ?? scope.profileId,
            profileName: selection.ownerProfileName ?? scope.profileName,
        });
    };

    const selectProfile = (selection: ScopeSelection) => {
        // Keep the project only if this profile still owns it; otherwise it would contradict the profile.
        const keepProject = scope.projectId !== null && scope.profileId === selection.id;
        onChange({
            ...scope,
            profileId: selection.id,
            profileName: selection.name,
            projectId: keepProject ? scope.projectId : null,
            projectName: keepProject ? scope.projectName : null,
        });
    };

    const clearProject = () => onChange({ ...scope, projectId: null, projectName: null });

    const clearProfile = () =>
        onChange({ ...scope, profileId: null, profileName: null, projectId: null, projectName: null });

    return (
        <div className={theme.scopeBar}>
            <div className={theme.scopeField}>
                <span className={theme.scopeFieldLabel}>Seller profile</span>
                <div className={theme.scopeFieldControl}>
                    {/* One segmented control: the clear segment reads as part of the picker it clears. */}
                    <ButtonGroup aria-label="Seller profile" className={theme.scopeButtonGroup}>
                        <DropdownButton
                            size="sm"
                            variant="tertiary"
                            icon={faUsersCrown}
                            label={scope.profileName ?? 'Any profile'}>
                            {/* No ScrollContainer — the picker scrolls its own list so the search stays pinned. */}
                            <ScopePicker
                                kind="profile"
                                selectedId={scope.profileId}
                                onChange={selectProfile}
                                overriddenIds={overriddenProfileIds}
                            />
                        </DropdownButton>
                        {scope.profileId != null && (
                            <Button
                                size="sm"
                                variant="tertiary"
                                icon={faXmark}
                                onClick={clearProfile}
                                title="Clear profile"
                            />
                        )}
                    </ButtonGroup>
                </div>
            </div>

            <div className={theme.scopeField}>
                <span className={theme.scopeFieldLabel}>Project</span>
                <div className={theme.scopeFieldControl}>
                    <ButtonGroup aria-label="Project" className={theme.scopeButtonGroup}>
                        <DropdownButton
                            size="sm"
                            variant="tertiary"
                            icon={faBuilding}
                            label={
                                scope.projectName ??
                                (scope.profileName ? `All ${scope.profileName} projects` : 'All projects')
                            }>
                            <ScopePicker
                                kind="project"
                                selectedId={scope.projectId}
                                onChange={selectProject}
                                overriddenIds={overriddenProjectIds}
                                // Narrowed to the chosen profile, so the list can only offer valid projects.
                                filterProfileId={scope.profileId}
                            />
                        </DropdownButton>
                        {scope.projectId != null && (
                            <Button
                                size="sm"
                                variant="tertiary"
                                icon={faXmark}
                                onClick={clearProject}
                                title="Clear project"
                            />
                        )}
                    </ButtonGroup>
                </div>
            </div>

            <div className={theme.scopeField}>
                <span className={theme.scopeFieldLabel}>Language</span>
                <Select
                    inputSize="sm"
                    options={[
                        { value: ANY_LANGUAGE, label: 'Any language' },
                        ...languages.map((x) => ({ value: x, label: x })),
                    ]}
                    value={scope.languageCode ?? ANY_LANGUAGE}
                    onChange={(value) => onChange({ ...scope, languageCode: value || null })}
                />
            </div>

        </div>
    );
};

export default ScopeBar;
